// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { IsoCurrencyCode } from '@/currency/constants/iso';
import { padAmountToMinorUnit } from '@/currency/utilities/serialize';
import {
  exceedsMaxCurrencyAmount,
  parseCurrencyAmount,
} from '@/currency/utilities/validate';
import { ChargeFormFields } from '@/project/components/charge-form-fields';
import {
  ChargeFormData,
  chargeFormSchema,
} from '@/project/types/charge-form-schema';
import { ChargeFormValue } from '@/project/utilities/generate-charges';
import { MAX_DOLLAR_VALUE_LABEL } from '@/utilities/currency';
import { zodResolver } from '@hookform/resolvers/zod';
import { Paper } from '@mui/material';
import { useForm } from 'react-hook-form';

interface Props {
  charge: ChargeFormValue;
  currencyCode: IsoCurrencyCode;
  onSave: (updated: ChargeFormValue) => void;
  onCancel: () => void;
}

export function ChargeEditForm(props: Props) {
  // A PER_UNIT charge with a null unit_price came from a plan with a
  // total_override in effect — the total is authoritative, not
  // qty × unit. Edit it like a FIXED charge: one "Amount" input, no
  // quantity math, and on save emit unit_price = null + total = amount.
  // Without this branch the form would render "Quantity × Unit price",
  // seed the unit field from total_price, and on submit compute
  // total = qty × total, silently multiplying the amount by the
  // quantity (e.g. $300 override × qty 4 becomes $1200 on edit).
  const editsAsAmount =
    props.charge.pricingMode !== 'PER_UNIT' || props.charge.unitPrice === null;
  const effectivePricingMode = editsAsAmount ? 'FIXED' : 'PER_UNIT';

  // The form field is named `unitPrice` for historical reasons but the
  // input is labeled "Amount" in FIXED-edit mode and feeds total_price
  // on save. Seed from total_price whenever unit_price is null. The
  // charge's single currency drives the locked currency selector.
  const seededAmount =
    props.charge.unitPrice?.amount ?? props.charge.totalPrice.amount;
  const form = useForm<ChargeFormData>({
    resolver: zodResolver(chargeFormSchema),
    defaultValues: {
      label: props.charge.label,
      quantity: String(props.charge.quantity),
      unitPrice: { amount: seededAmount, isoCode: props.currencyCode },
      servicePeriodStart: props.charge.servicePeriodStart,
      servicePeriodEnd: props.charge.servicePeriodEnd,
      expectedBillableDate: props.charge.expectedBillableDate,
    },
  });

  const handleSave = form.handleSubmit((data) => {
    const rawUnitPrice = data.unitPrice.amount.trim();
    const editedQty = parseInt(data.quantity ?? '', 10) || 0;
    const editedUnitPrice = parseCurrencyAmount(rawUnitPrice);
    // Quantity is not user-editable in FIXED-edit mode (the form only
    // renders the Amount input), so skip the qty-validity check there.
    const isQuantityInvalid = !editsAsAmount && editedQty <= 0;
    const isPriceMissing = rawUnitPrice === '';
    const isPriceInvalid =
      isPriceMissing ||
      editedUnitPrice === null ||
      editedUnitPrice.isNegative();

    if (isQuantityInvalid) {
      form.setError('quantity', {
        type: 'client',
        message: 'Quantity must be at least 1',
      });
    }
    if (isPriceInvalid) {
      form.setError('unitPrice.amount', {
        type: 'client',
        message: isPriceMissing
          ? editsAsAmount
            ? 'Amount is required'
            : 'Unit price is required'
          : editsAsAmount
            ? 'Amount cannot be negative'
            : 'Unit price cannot be negative',
      });
    }
    // FIXED-edit: total = amount, unit = null (no strict × check).
    // PER_UNIT-edit: total = qty × unit, unit preserved for the
    //   qty × unit == total BE strict rule.
    const editedTotalAmount =
      editedUnitPrice === null
        ? null
        : editsAsAmount
          ? editedUnitPrice
          : editedUnitPrice.times(editedQty);
    const isAmountOverBound =
      editedUnitPrice !== null &&
      editedTotalAmount !== null &&
      !isPriceInvalid &&
      (exceedsMaxCurrencyAmount(editedUnitPrice) ||
        exceedsMaxCurrencyAmount(editedTotalAmount));
    if (isAmountOverBound) {
      form.setError('unitPrice.amount', {
        type: 'client',
        message: editsAsAmount
          ? `Amount must not exceed ${MAX_DOLLAR_VALUE_LABEL}`
          : `Charge total (qty × unit price) must not exceed ${MAX_DOLLAR_VALUE_LABEL}`,
      });
    }
    if (
      isQuantityInvalid ||
      isPriceInvalid ||
      isAmountOverBound ||
      editedUnitPrice === null ||
      editedTotalAmount === null
    ) {
      return;
    }

    const editedTotal = {
      amount: padAmountToMinorUnit(editedTotalAmount, props.currencyCode),
      isoCode: props.currencyCode,
    };
    const editedUnitPriceOut = editsAsAmount
      ? null
      : {
          amount: padAmountToMinorUnit(editedUnitPrice, props.currencyCode),
          isoCode: props.currencyCode,
        };

    props.onSave({
      ...props.charge,
      label: data.label,
      quantity: editedQty,
      unitPrice: editedUnitPriceOut,
      totalPrice: editedTotal,
      servicePeriodStart: data.servicePeriodStart,
      servicePeriodEnd: data.servicePeriodEnd,
      expectedBillableDate: data.expectedBillableDate,
      isCustomModified: true,
    });
  });

  return (
    <Paper variant="outlined" sx={{ mt: 1, p: 1.5 }}>
      <ChargeFormFields
        control={form.control}
        pricingMode={effectivePricingMode}
        currencyCode={props.currencyCode}
        saveLabel="Update charge"
        onSave={handleSave}
        onCancel={props.onCancel}
      />
    </Paper>
  );
}
